import React, { FC, useEffect } from 'react'
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom'
import { cn } from '@bem-react/classname'
import { useTranslation } from 'react-i18next'

import { Memo } from '@/hoc/Memo'
import { useAppDispatch, useAppSelector } from '@/core/store/hooks'
import { getItem } from '@/utils/persistanceStorage'
import {
  changeAuth,
  changeRole,
  getCurrentUserThunk,
  getUserRoleThunk,
  setConnectedResellerId,
} from '../AccountPage/store/accont.slice'
import { currentUserSelector } from '../AccountPage/store/accont.selector'

import { Block } from '@/components/Block'

// Pages
import { ErrorPage } from '../ErrorPage'
import {
  LicensesPage,
  RenewPage,
  SubscriptionPage,
  PaymentPage,
  CustomerInfo,
} from '../LicensesPage'
import { AccountPage } from '../AccountPage'
import { CreateLicense } from '../AddLicense'
import { ProductsPage } from '../ProductsPage'

import './styles/PrivateAppPage.scss'
import {
  AccessTokenModel,
  RoleUserModel,
  TokenModel,
} from '@/core/services/Token/model/TokenModel'
import jwtDecode from 'jwt-decode'
import { StatisticPage } from '../StatisticPage'
import useBackendError from '@/hooks/useBackendError'
import { changeLoading } from '@/core/services/Loading/store/loading.slice'

const cnPrivate = cn('Private')

export const PrivateAppPage: FC = Memo(() => {
  const { t } = useTranslation('translation')
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const currentUser = useAppSelector(currentUserSelector)

  const [setBackendError] = useBackendError()

  useEffect(() => {
    const token: TokenModel | null = getItem('token')

    if (token) {
      dispatch(changeAuth(true))
      dispatch(getCurrentUserThunk())

      const accessToken = token.access_token
      const decode: AccessTokenModel = jwtDecode(accessToken)
      dispatch(changeRole(decode.RoleId))
    } else {
      navigate('/')
    }
  }, [])

  useEffect(() => {
    if (pathname && pathname.indexOf('/cabinet/licenses/') === -1) {
      localStorage.removeItem('form')
    }
  }, [pathname])

  const getCurrentUserRole = async (userId: string) => {
    try {
      changeLoading(true)
      await dispatch(getUserRoleThunk(userId)).unwrap()
    } catch (e) {
      setBackendError(e)
    } finally {
      changeLoading(false)
    }
  }

  useEffect(() => {
    const userId = currentUser?.id

    if (!userId) {
      return
    }

    getCurrentUserRole(userId)
  }, [currentUser])

  useEffect(() => {
    const token: TokenModel | null = getItem('token')

    if (!token) {
      return
    }

    const accessToken = token.access_token
    const decode: AccessTokenModel = jwtDecode(accessToken)
    dispatch(changeRole(decode.RoleId) || RoleUserModel.User)
    dispatch(setConnectedResellerId(decode?.ResellerId || ''))
  }, [])

  return (
    <>
      {currentUser && !currentUser.emailConfirmed && (
        <Block type='info' className={cnPrivate('notice')}>
          {t(
            'Dear customer, Please follow to the email address entered during registration to pass the verification of the account. Thank you for choosing Dinamica Generale! The new Register tool will allow you manage all your cloud licenses in the easiest way.',
          )}
        </Block>
      )}
      <Routes>
        <Route path='/licenses' element={<LicensesPage />} />
        <Route path='/statistic' element={<StatisticPage />} />
        <Route path='/account' element={<AccountPage />} />
        {/* <Route path='/add-license' element={<AddLicense />} /> */}
        <Route path='/licenses/create' element={<CreateLicense />} />
        <Route path='/licenses/renew' element={<RenewPage />} />
        <Route path='/licenses/subscription' element={<SubscriptionPage />} />
        <Route path='/licenses/payment' element={<PaymentPage />} />
        <Route path='/licenses/add-customer-info' element={<CustomerInfo />} />
        <Route path='/licenses/products' element={<ProductsPage />} />
        <Route path='*' element={<ErrorPage />} />
      </Routes>
    </>
  )
})
